import { groupBy, mapValues, sumBy } from 'lodash-es';
import { TicketOptionWithQuantity } from '@repo/types';
import { isWaitingListTicketOption } from '@repo/widget-utils/cart/cartUtils';
import { isUnboundedCapacity } from '@repo/widget-utils/booking/domains/shared/utils';

function getNumberOfTravelers(quantities: TicketOptionWithQuantity[]) {
    return sumBy(quantities, getNumberOfTravelersForTicketOption);
}

function isTicketOptionCollectionMode(ticketOption: TicketOptionWithQuantity) {
    return ticketOption.productInstances.some((x) => x.productInstanceCollectionId);
}

function isTicketOptionJoinedTrip(ticketOption: TicketOptionWithQuantity) {
    const firstProductInstance =
        ticketOption.productInstances.length > 0 ? ticketOption.productInstances[0] : null;
    return firstProductInstance &&
        typeof firstProductInstance.productInstanceCollectionMinEntrants === 'number'
        ? true
        : false;
}

function getNumberOfTravelersForTicketOption(ticketOption: TicketOptionWithQuantity) {
    return ticketOption.quantity * ticketOption.occupancy;
}

function getNumberOfTravelersPerProductInstance(ticketOptions: TicketOptionWithQuantity[]) {
    return mapValues(
        groupBy(ticketOptions, (to) => to.productInstances[0]?.id),
        (to) => getNumberOfTravelers(to),
    );
}

function getCollectionModeMaxTravellersPerBookingForTicketOption(
    ticketOption: TicketOptionWithQuantity,
) {
    const productInstanceWithMaxEntrantsPerBooking = ticketOption.productInstances.find(
        (x) => x.productInstanceCollectionMaxEntrantsPerBooking !== null,
    );
    return (
        productInstanceWithMaxEntrantsPerBooking?.productInstanceCollectionMaxEntrantsPerBooking ??
        null
    );
}

function getCollectionModeMaxTravellersForTicketOption(ticketOption: TicketOptionWithQuantity) {
    const productInstanceWithMaxEntrantsPerBooking = ticketOption.productInstances.find(
        (x) => x.productInstanceCollectionMaxEntrants !== null,
    );
    return productInstanceWithMaxEntrantsPerBooking?.productInstanceCollectionMaxEntrants ?? null;
}

function getCapacityForJoinedMode(ticketOptions: TicketOptionWithQuantity[]) {
    const [firstTicketOption] = ticketOptions;

    const maxTravellersPerBooking =
        getCollectionModeMaxTravellersPerBookingForTicketOption(firstTicketOption);

    const maxTravellers = getCollectionModeMaxTravellersForTicketOption(firstTicketOption);

    // Figure out how many travellers have already been reserved by summarizing the currently consumed capacity for all ticket options
    const currentlyConsumedCapacity = sumBy(
        ticketOptions,
        (ticketOption) => ticketOption.currentConsumedCapacity,
    );

    const globalMaxTravellers = maxTravellers ?? 0;
    // The current global capacity is the global max capacity minus the travellers that have already booked
    const globalMaxCapacity = globalMaxTravellers - currentlyConsumedCapacity;
    if (!maxTravellersPerBooking) return globalMaxCapacity;

    // If we got a max capacity per booking, we use that instead of the global, but make sure it's not more than the global max capacity
    return Math.min(maxTravellersPerBooking, globalMaxCapacity);
}

export type QuantityError = {
    id: string;
    error: 'minEntrants' | 'capacity';
    value: number;
    type: 'individual' | 'total';
};

function validateMinEntrantsForProductInstanceCollection(
    ticketOptions: TicketOptionWithQuantity[],
    errors: QuantityError[],
) {
    const productInstanceCollectionMinEntrants =
        ticketOptions[0].productInstances.find(
            (pi) => typeof pi.productInstanceCollectionMinEntrants === 'number',
        )?.productInstanceCollectionMinEntrants ?? 0;
    const numberOfTravelers = ticketOptions.reduce(
        (acc, cur) => acc + cur.quantity * cur.occupancy,
        0,
    );

    if (numberOfTravelers < productInstanceCollectionMinEntrants) {
        errors.push({
            id: ticketOptions[0].productInstances[0].productInstanceCollectionId ?? '',
            error: 'minEntrants',
            value: productInstanceCollectionMinEntrants,
            type: 'total',
        });
    }
}

function validateCapacityForJoinedMode(
    ticketOptions: TicketOptionWithQuantity[],
    numberOfTravelers: number,
    errors: QuantityError[],
) {
    const [firstTicketOption] = ticketOptions;

    const capacity = getCapacityForJoinedMode(ticketOptions);

    if (!capacity || numberOfTravelers > capacity) {
        errors.push({
            id: firstTicketOption.productInstances[0]?.productInstanceCollectionId ?? '',
            error: 'capacity',
            value: capacity ?? 0,
            type: 'total',
        });
    }
}

// SB-933: the per-entry capacity Bilberry returns reflects the seats remaining
// on the shared physical resource (already net of consumption by other tours
// drawing from it). It can be tighter than max_entrants(_per_booking), so the
// joined-mode check above isn't sufficient. Total across all sibling ticket
// options must also respect each one's reported capacity.
function validateCollectionResourceCapacityForCart(
    ticketOptions: TicketOptionWithQuantity[],
    numberOfTravelers: number,
    errors: QuantityError[],
) {
    // The lowest non-unbounded capacity across the group is the binding total.
    let resourceCapacity: number | null = null;
    for (const to of ticketOptions) {
        for (const pi of to.productInstances) {
            if (isUnboundedCapacity(pi.capacity)) {
                continue;
            }
            if (resourceCapacity === null || pi.capacity < resourceCapacity) {
                resourceCapacity = pi.capacity;
            }
        }
    }

    if (resourceCapacity === null) return;

    if (numberOfTravelers > resourceCapacity) {
        for (const to of ticketOptions) {
            errors.push({
                id: to.ticketCategoryId,
                error: 'capacity',
                value: resourceCapacity,
                type: 'total',
            });
        }
    }
}

function validateIndividualProductRequirements(
    ticketOptions: TicketOptionWithQuantity[],
    numberOfTravelersPerProductInstance: Record<string, number>,
    requireExplicitlyAllowedZeroQuantity: boolean,
    collectionHasActivity: boolean,
    errors: QuantityError[],
) {
    ticketOptions.forEach((to) => {
        const quantity = to.quantity * to.occupancy;
        if (quantity === 0 && !requireExplicitlyAllowedZeroQuantity && !collectionHasActivity)
            return;

        const hasMinEntrantsForTicketOption =
            to.minEntrants !== null && typeof to.minEntrants === 'number';

        const isCurrentQuantityLessThanMinEntrantsForTicketOption =
            hasMinEntrantsForTicketOption && quantity < to.minEntrants;

        if (isCurrentQuantityLessThanMinEntrantsForTicketOption) {
            errors.push({
                id: to.ticketCategoryId,
                error: 'minEntrants',
                type: 'individual',
                value: to.minEntrants,
            });
        } else {
            to.productInstances.forEach((pi) => {
                const requiredNumberOfTravellersForThisInstance = pi.minEntrants ?? 0;
                const currentTravellersForThisInstance = numberOfTravelersPerProductInstance[pi.id];
                const hasTravellersForThisProductInstance = currentTravellersForThisInstance > 0;

                const validateRequiredNumberOfTravellers =
                    hasTravellersForThisProductInstance ||
                    requireExplicitlyAllowedZeroQuantity ||
                    collectionHasActivity;
                const hasLessTravellersThanRequired =
                    currentTravellersForThisInstance < requiredNumberOfTravellersForThisInstance;

                const hasError =
                    validateRequiredNumberOfTravellers && hasLessTravellersThanRequired;

                if (hasError) {
                    errors.push({
                        id: to.ticketCategoryId,
                        error: 'minEntrants',
                        type: 'individual',
                        value: pi.minEntrants ?? 0,
                    });
                }
            });
        }
    });
}

function checkCapacityConstraints(
    ticketOptions: TicketOptionWithQuantity[],
    numberOfTravelers: number,
    isJoinedMode: boolean,
    requireAllValid: boolean,
    errors: QuantityError[],
) {
    if (isJoinedMode) {
        validateCapacityForJoinedMode(ticketOptions, numberOfTravelers, errors);
    }

    // SB-933/SB-944/SB-956: gate the shared-resource cap on require_all_valid.
    // The flag literally controls zero-quantity min_entrants enforcement, but
    // we use it as a proxy for "per-availability capacity is a shared pool,
    // not an independent per-category dimension" — see the longer rationale
    // in activityQuantities.buildActivityContext (this is the cart-time
    // mirror, must stay in sync).
    if (requireAllValid) {
        validateCollectionResourceCapacityForCart(ticketOptions, numberOfTravelers, errors);
    }

    ticketOptions.forEach((to) => {
        const isWaitingListItem = isWaitingListTicketOption(to);

        if (isWaitingListItem) {
            return;
        }

        let travelers = numberOfTravelers;
        const isProductInstanceCollection = to.productInstances.some(
            (p) => p.productInstanceCollectionId !== null,
        );

        if (isProductInstanceCollection) {
            travelers = to.quantity * to.occupancy;
        }

        to.productInstances.forEach((pi) => {
            if (travelers > pi.capacity) {
                errors.push({
                    id: to.ticketCategoryId,
                    error: 'capacity',
                    type: isProductInstanceCollection ? 'individual' : 'total',
                    value: pi.capacity,
                });
            }

            if (
                typeof pi.productInstanceCollectionMaxEntrantsPerBooking === 'number' &&
                travelers > pi.productInstanceCollectionMaxEntrantsPerBooking
            ) {
                errors.push({
                    id: to.ticketCategoryId,
                    error: 'capacity',
                    type: 'total',
                    value: pi.productInstanceCollectionMaxEntrantsPerBooking,
                });
            }
        });

        if (to.capacity !== Number.MAX_VALUE && travelers > to.capacity) {
            errors.push({
                id: to.ticketCategoryId,
                error: 'capacity',
                type: isProductInstanceCollection ? 'individual' : 'total',
                value: to.capacity,
            });
        }
    });
}

export function verifyQuantities(priceQuantities: TicketOptionWithQuantity[]) {
    const errors: QuantityError[] = [];
    const groupedTicketOptions = groupBy(priceQuantities, (pq) =>
        pq.productInstances.map((pi) => pi.productInstanceCollectionId ?? pi.id).join(''),
    );

    Object.values(groupedTicketOptions).forEach((ticketOptions) => {
        const [firstTicketOption] = ticketOptions;

        const isJoinedTrip = isTicketOptionJoinedTrip(firstTicketOption);
        const numberOfTravelers = getNumberOfTravelers(ticketOptions);
        const numberOfTravelersPerProductInstance =
            getNumberOfTravelersPerProductInstance(ticketOptions);

        const isProjectCollectionMode = isTicketOptionCollectionMode(firstTicketOption);

        if (isProjectCollectionMode) {
            validateMinEntrantsForProductInstanceCollection(ticketOptions, errors);
        }

        const projectCollectionRequireAllValid = ticketOptions.some((t) =>
            t.productInstances.some((pi) => pi.productInstanceCollectionValidateAll),
        );

        const skipIndividualProductRequirementValidation =
            isProjectCollectionMode && isJoinedTrip && !projectCollectionRequireAllValid;
        const requireIndividialProductRequirementValidation =
            !isProjectCollectionMode || !skipIndividualProductRequirementValidation;
        if (requireIndividialProductRequirementValidation) {
            const requireExplicitlyAllowedZeroQuantity =
                isJoinedTrip && projectCollectionRequireAllValid;
            // Mirror of activityQuantities collection-aware zero-skip: in
            // non-joined-trip collection mode (accommodation Unit + extras),
            // a sibling's pi.minEntrants must fire once the user has engaged
            // with the collection, even if the sibling itself is at zero.
            const collectionHasActivity =
                isProjectCollectionMode &&
                !isJoinedTrip &&
                ticketOptions.some((t) => t.quantity > 0);
            validateIndividualProductRequirements(
                ticketOptions,
                numberOfTravelersPerProductInstance,
                requireExplicitlyAllowedZeroQuantity,
                collectionHasActivity,
                errors,
            );
        }

        checkCapacityConstraints(
            ticketOptions,
            numberOfTravelers,
            isJoinedTrip,
            projectCollectionRequireAllValid,
            errors,
        );
    });

    return errors;
}
